'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { CAPABILITIES, type Capability } from '../_lib/capabilities';
import { CAPABILITY_ICONS } from './icons';

/**
 * NIVEL 2 — global capability bar. Always visible on every canon route.
 *
 * Behavior (per PR1.5 guidance):
 *   - Hub (`/`)        → all 5 quiet, none active.
 *   - Composer         → all 5 quiet, none active (Composer is Mundo B,
 *                        not one of the 5 capabilities).
 *   - Launch Pad       → current capability active + moved to position 1
 *                        (active-first browser-tab semantics).
 *
 * Distinct from the NIVEL 3 zone sub-bar (`SubBar` → `ZoneTabs`):
 * `.cap-tab` here vs `.section-tab` there.
 */
function activeCapabilityId(pathname: string): Capability['id'] | null {
  for (const cap of CAPABILITIES) {
    if (pathname === cap.href || pathname.startsWith(cap.href + '/')) return cap.id;
  }
  return null;
}

export function CapabilityBar() {
  const pathname = usePathname() ?? '/';
  const activeId = activeCapabilityId(pathname);

  // Active-first: move the active capability to position 1, keep the rest
  // in canonical order.
  const ordered = activeId
    ? [
        ...CAPABILITIES.filter((c) => c.id === activeId),
        ...CAPABILITIES.filter((c) => c.id !== activeId),
      ]
    : CAPABILITIES;

  return (
    <nav className="row-caps" aria-label="Capabilities">
      <div className="cap-scroll" role="tablist">
        {ordered.map((cap) => {
          const active = cap.id === activeId;
          return (
            <Link
              key={cap.id}
              href={cap.href}
              role="tab"
              aria-selected={active}
              className={`cap-tab${active ? ' active' : ''}`}
            >
              {CAPABILITY_ICONS[cap.id]}
              {cap.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
